/**
 * Dynamic Facet Loader - Bypasses Shopify Liquid's 100-item limitation
 * Fetches all filter values via Storefront API and dynamically renders them
 */

class DynamicFacetLoader {
  constructor() {
    this.storefrontAccessToken = null;
    this.collectionHandle = null;
    this.searchQuery = null;
    this.isSearchPage = false;
    this.filterCache = new Map();
    this.loadingFilters = new Set(); // Track filters currently being loaded
    this.init();
  }

  init() {
    console.log('🎬 DynamicFacetLoader initializing...');
    // Get Storefront API token from meta tag
    const tokenMeta = document.querySelector('meta[name="shopify-storefront-api-token"]');
    this.storefrontAccessToken = tokenMeta ? tokenMeta.content : null;

    if (!this.storefrontAccessToken) {
      console.warn('Storefront API token not found. Dynamic filter loading disabled.');
      return;
    }

    // Determine if we're on a collection or search page
    this.isSearchPage = window.location.pathname.includes('/search');
    
    if (this.isSearchPage) {
      const urlParams = new URLSearchParams(window.location.search);
      this.searchQuery = urlParams.get('q') || '';
    } else {
      // Extract collection handle from URL or page data
      const collectionMeta = document.querySelector('[data-collection-handle]');
      this.collectionHandle = collectionMeta ? collectionMeta.dataset.collectionHandle : null;
    }

    // Initialize filters after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeFilters();
        this.updateActiveFacets();
      });
    } else {
      this.initializeFilters();
      this.updateActiveFacets();
    }
  }

  async initializeFilters() {
    // Find filters that need dynamic loading (marked with data attribute)
    const filtersToLoad = document.querySelectorAll('[data-dynamic-filter="true"]');
    
    for (const filterElement of filtersToLoad) {
      const filterId = filterElement.dataset.filterId;
      const filterLabel = filterElement.dataset.filterLabel;
      
      await this.loadAndRenderFilter(filterElement, filterId, filterLabel);
    }
  }

  /**
   * Reinitialize filters after facet updates (when filters are re-rendered via AJAX)
   * This ensures dynamic loading persists after filter selections
   */
  reinitializeFilters() {
    console.log('🔄 Reinitializing dynamic filters after page update...');
    
    // No delay needed here - facets.js already handles the timing
    this.initializeFilters();
    this.updateActiveFacets();
  }

  async loadAndRenderFilter(filterElement, filterId, filterLabel) {
    // Check if this filter is currently being loaded (prevent duplicate API calls)
    const loadingKey = `${filterId}_${filterLabel}`;
    if (this.loadingFilters.has(loadingKey)) {
      console.log(`Filter "${filterLabel}" is already being loaded, skipping duplicate...`);
      return;
    }

    this.loadingFilters.add(loadingKey);

    // Show loading indicator
    const loadingIndicator = filterElement.querySelector('.facet-dynamic-loading');
    if (loadingIndicator) {
      loadingIndicator.style.display = 'block';
    }

    try {
      // Check cache first
      const cacheKey = `${this.collectionHandle || this.searchQuery}_${filterId}`;
      let filterData = this.filterCache.get(cacheKey);

      if (!filterData) {
        console.log(`📡 Fetching filter data for "${filterLabel}" (not in cache)...`);
        // Fetch filter data from Storefront API
        filterData = await this.fetchFilterValues();
        this.filterCache.set(cacheKey, filterData);
      } else {
        console.log(`⚡ Using cached filter data for "${filterLabel}"`);
      }

      // Find the specific filter in the response
      const filter = filterData.find(f => f.id === filterId || f.label.toLowerCase() === filterLabel.toLowerCase());
      
      if (filter && filter.values && filter.values.length > 0) {
        this.renderFilterValues(filterElement, filter);
      }
    } catch (error) {
      console.error('Failed to load dynamic filter:', error);
      this.showError(filterElement, 'Failed to load all filter options. Please refresh the page.');
    } finally {
      // Hide loading indicator
      if (loadingIndicator) {
        loadingIndicator.style.display = 'none';
      }
      
      // Remove from loading set
      this.loadingFilters.delete(loadingKey);
    }
  }

  showError(filterElement, message) {
    const loadingIndicator = filterElement.querySelector('.facet-dynamic-loading');
    if (loadingIndicator) {
      loadingIndicator.innerHTML = `
        <span style="color: rgba(var(--color-error, 200, 50, 50), 1);">${message}</span>
      `;
      loadingIndicator.style.display = 'block';
    }
  }

  async fetchFilterValues() {
    // First, get the basic filter structure (limited to 100 values)
    const query = this.isSearchPage ? this.buildSearchQuery() : this.buildCollectionQuery();
    
    const response = await fetch(`${window.Shopify.routes.root}api/${this.getApiVersion()}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken
      },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    let filters = [];
    if (this.isSearchPage) {
      filters = result.data?.search?.productFilters || [];
    } else {
      filters = result.data?.collection?.products?.filters || [];
    }

    // For filters that might have more than 100 values, fetch products and build values manually
    // This is specifically for metafield-based filters like "dodatki"
    const enrichedFilters = await this.enrichMetafieldFilters(filters);
    
    return enrichedFilters;
  }

  async enrichMetafieldFilters(filters) {
    // Find filters that are metafield-based and might have more than 100 values
    for (const filter of filters) {
      // Check if this is a metafield filter with close to 100 values (likely truncated)
      if (filter.values && filter.values.length >= 90) {
        console.log(`⚡ Enriching filter "${filter.label}" with ${filter.values.length} values - fetching more from products...`);
        
        try {
          const allValues = await this.fetchAllMetafieldValues(filter);
          if (allValues.length > filter.values.length) {
            console.log(`✅ Found ${allValues.length} total values for "${filter.label}" (was ${filter.values.length})`);
            filter.values = allValues;
          }
        } catch (error) {
          console.warn(`Failed to enrich filter "${filter.label}":`, error);
          // Keep original values on error
        }
      }
    }
    
    return filters;
  }

  async fetchAllMetafieldValues(filter) {
    // Extract metafield info from filter ID if it's a metafield filter
    // Format: "filter.p.m.custom.dodatki" or similar
    const isMetafieldFilter = filter.id && filter.id.includes('.m.');
    
    if (!isMetafieldFilter) {
      // Not a metafield filter, can't enrich
      return filter.values;
    }

    // Extract namespace and key from filter ID
    // Example: "filter.p.m.custom.dodatki" -> namespace: "custom", key: "dodatki"
    const parts = filter.id.split('.');
    const namespaceIndex = parts.indexOf('m') + 1;
    const namespace = parts[namespaceIndex];
    const key = parts[namespaceIndex + 1];

    if (!namespace || !key) {
      return filter.values;
    }

    // Fetch all products with pagination and extract unique metafield values
    const uniqueValues = new Map(); // Use map to track unique values with their counts
    let hasNextPage = true;
    let cursor = null;
    let pageCount = 0;
    const MAX_PAGES = 10; // Limit to prevent infinite loops (10 pages * 250 products = 2500 products)

    while (hasNextPage && pageCount < MAX_PAGES) {
      const productQuery = this.buildProductMetafieldQuery(namespace, key, cursor);
      
      const response = await fetch(`${window.Shopify.routes.root}api/${this.getApiVersion()}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken
        },
        body: JSON.stringify({ query: productQuery })
      });

      if (!response.ok) {
        break; // Stop on error
      }

      const result = await response.json();
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        break;
      }

      const products = this.isSearchPage 
        ? result.data?.search?.nodes || []
        : result.data?.collection?.products?.nodes || [];
      
      const pageInfo = this.isSearchPage
        ? result.data?.search?.pageInfo
        : result.data?.collection?.products?.pageInfo;

      // Extract metafield values from products
      products.forEach(product => {
        const metafield = product.metafield;
        if (metafield && metafield.value) {
          // Handle different metafield types
          let values = [];
          
          if (metafield.type === 'list.single_line_text_field') {
            // Parse JSON array
            try {
              values = JSON.parse(metafield.value);
            } catch (e) {
              values = [metafield.value];
            }
          } else {
            values = [metafield.value];
          }

          values.forEach(value => {
            if (value && value.trim()) {
              const normalizedValue = value.trim();
              if (!uniqueValues.has(normalizedValue)) {
                uniqueValues.set(normalizedValue, 1);
              } else {
                uniqueValues.set(normalizedValue, uniqueValues.get(normalizedValue) + 1);
              }
            }
          });
        }
      });

      hasNextPage = pageInfo?.hasNextPage || false;
      cursor = pageInfo?.endCursor;
      pageCount++;
    }

    // Convert unique values back to filter value format
    const enrichedValues = Array.from(uniqueValues.entries()).map(([label, count]) => {
      // Create input JSON that matches Shopify's filter format
      const input = {
        productMetafield: {
          namespace: namespace,
          key: key,
          value: label
        }
      };

      // Generate a unique ID
      const id = `filter.p.m.${namespace}.${key}.${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

      return {
        id: id,
        label: label,
        count: count,
        input: JSON.stringify(input)
      };
    });

    // Sort by label alphabetically (with natural number sorting)
    enrichedValues.sort((a, b) => {
      return a.label.localeCompare(b.label, undefined, { 
        sensitivity: 'base',
        numeric: true // Handle numbers naturally (e.g., "2" before "10")
      });
    });

    console.log(`🔤 Sorted ${enrichedValues.length} values alphabetically`);

    return enrichedValues;
  }

  buildProductMetafieldQuery(namespace, key, cursor) {
    const afterClause = cursor ? `, after: "${cursor}"` : '';
    
    if (this.isSearchPage) {
      return `
        query GetProductMetafields {
          search(
            query: "${this.escapeGraphQL(this.searchQuery)}"
            types: [PRODUCT]
            first: 250${afterClause}
          ) {
            nodes {
              ... on Product {
                id
                metafield(namespace: "${namespace}", key: "${key}") {
                  value
                  type
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;
    } else {
      return `
        query GetProductMetafields {
          collection(handle: "${this.collectionHandle}") {
            products(first: 250${afterClause}) {
              nodes {
                id
                metafield(namespace: "${namespace}", key: "${key}") {
                  value
                  type
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        }
      `;
    }
  }

  buildCollectionQuery() {
    return `
      query GetCollectionFilters {
        collection(handle: "${this.collectionHandle}") {
          products(first: 1) {
            filters {
              id
              label
              type
              presentation
              values {
                id
                label
                count
                input
                image {
                  image {
                    url
                    altText
                  }
                }
                swatch {
                  color
                  image {
                    previewImage {
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
  }

  buildSearchQuery() {
    return `
      query GetSearchFilters {
        search(query: "${this.escapeGraphQL(this.searchQuery)}", first: 1, types: [PRODUCT]) {
          productFilters {
            id
            label
            type
            presentation
            values {
              id
              label
              count
              input
              image {
                image {
                  url
                  altText
                }
              }
              swatch {
                color
                image {
                  previewImage {
                    url
                  }
                }
              }
            }
          }
        }
      }
    `;
  }

  renderFilterValues(filterElement, filter) {
    const listContainer = filterElement.querySelector('ul.facets__list, ul.facets-layout-list');
    
    if (!listContainer) {
      console.warn('Filter list container not found');
      return;
    }

    // Get current active filter values from URL
    const activeValues = this.getActiveFilterValues(filter.id);
    
    // Get the template for filter items (use first existing item as template)
    const templateItem = listContainer.querySelector('li.facets__item');
    const showFilterCount = templateItem ? templateItem.querySelector('.facet__results') !== null : true;

    // Sort filter values alphabetically before rendering
    const sortedValues = [...filter.values].sort((a, b) => {
      return a.label.localeCompare(b.label, undefined, { 
        sensitivity: 'base',
        numeric: true // Handle numbers naturally (e.g., "2" before "10")
      });
    });

    console.log(`🎨 Rendering ${sortedValues.length} filter values for "${filter.label}" in sorted order`);

    // Clear existing dynamically-added items (keep Liquid-rendered ones initially)
    const existingValues = new Set();
    const existingItems = listContainer.querySelectorAll('li.facets__item');
    existingItems.forEach(item => {
      const input = item.querySelector('input[type="checkbox"]');
      if (input) {
        existingValues.add(input.value);
      }
    });

    // Remove all existing items - we'll re-render everything in sorted order
    listContainer.innerHTML = '';

    // Render all filter values in sorted order
    sortedValues.forEach((value, index) => {
      const listItem = this.createFilterItem(value, filter, showFilterCount, activeValues, index);
      listContainer.appendChild(listItem);
    });

    // Update the count of items and ensure show more button works
    this.updateShowMoreButton(filterElement);
    
    // Re-initialize search functionality if it exists
    if (typeof filterList === 'function') {
      const searchInput = filterElement.querySelector('.facet-search-input');
      if (searchInput) {
        // Trigger the search to include new items
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // Mark as loaded
    filterElement.setAttribute('data-dynamic-loaded', 'true');
  }

  createFilterItem(value, filter, showFilterCount, activeValues, index) {
    const li = document.createElement('li');
    li.className = 'list-menu__item facets__item';
    
    // Parse the input JSON to get the actual filter parameter
    let inputData;
    try {
      inputData = typeof value.input === 'string' ? JSON.parse(value.input) : value.input;
    } catch (e) {
      inputData = value.input;
    }
    
    // Determine if this value is disabled (count is 0 and not active)
    const filterValue = this.getFilterValue(inputData);
    const isDisabled = value.count === 0 && !activeValues.has(filterValue);
    const isActive = activeValues.has(filterValue);

    // Generate unique input ID
    const inputId = `Filter-${filter.id}-${index}-dynamic-${Date.now()}`;
    
    // Create checkbox input
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'field__checkbox';
    checkbox.id = inputId;
    checkbox.name = this.getFilterParamName(filter, inputData);
    checkbox.value = filterValue;
    
    if (isActive) {
      checkbox.checked = true;
    }
    if (isDisabled) {
      checkbox.disabled = true;
    }

    // Create label
    const label = document.createElement('label');
    label.htmlFor = inputId;
    label.className = `facets__label facet-checkbox${isDisabled ? ' disabled' : ''}${isActive ? ' active' : ''}`;

    // Create label content with count
    const labelContent = `
      <span class="facet-checkbox__text" aria-hidden="true">
        <span class="facet-checkbox__text-label">${this.escapeHtml(value.label)}</span>${showFilterCount ? ` <span class="facet__results">(${value.count})</span>` : ''}
      </span>
      <span class="visually-hidden">
        ${this.escapeHtml(value.label)} ${showFilterCount ? `(${value.count} ${value.count === 1 ? 'product' : 'products'})` : ''}
      </span>
    `;
    
    label.innerHTML = labelContent;

    li.appendChild(checkbox);
    li.appendChild(label);

    return li;
  }

  getFilterParamName(filter, inputData) {
    // Generate the proper parameter name based on filter type
    if (inputData.productType) {
      return 'filter.p.product_type';
    } else if (inputData.productVendor) {
      return 'filter.p.vendor';
    } else if (inputData.variantOption) {
      return `filter.v.option.${inputData.variantOption.name}`;
    } else if (inputData.tag) {
      return 'filter.p.tag';
    } else if (inputData.available !== undefined) {
      return 'filter.v.availability';
    } else if (inputData.price) {
      return 'filter.v.price';
    } else if (inputData.productMetafield) {
      // Handle metafield filters
      return `filter.p.m.${inputData.productMetafield.namespace}.${inputData.productMetafield.key}`;
    }
    
    // Default fallback
    return `${filter.id}`;
  }

  getFilterValue(inputData) {
    // Handle string input (should be parsed before calling this)
    if (typeof inputData === 'string') {
      try {
        inputData = JSON.parse(inputData);
      } catch (e) {
        return inputData; // Return as-is if not JSON
      }
    }
    
    if (inputData.productType) {
      return inputData.productType;
    } else if (inputData.productVendor) {
      return inputData.productVendor;
    } else if (inputData.variantOption) {
      return inputData.variantOption.value; 
    } else if (inputData.tag) {
      return inputData.tag;
    } else if (inputData.available !== undefined) {
      return inputData.available ? '1' : '0';
    } else if (inputData.productMetafield && inputData.productMetafield.value) {
      return inputData.productMetafield.value;
    }
    
    // Fallback for unknown filter types
    return '';
  }

  getActiveFilterValues(filterId) {
    const activeValues = new Set();
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check URL params for active filters
    for (const [key, value] of urlParams.entries()) {
      if (key.includes('filter.') && value && value.trim() !== '') {
        // Only add non-empty values to avoid false matches
        activeValues.add(value);
      }
    }
    
    return activeValues;
  }

  updateShowMoreButton(filterElement) {
    const showMoreButton = filterElement.querySelector('show-more-button');
    
    if (showMoreButton) {
      const allItems = filterElement.querySelectorAll('li.facets__item');
      const threshold = parseInt(filterElement.dataset.showMoreThreshold) || 10;
      
      if (allItems.length > threshold) {
        // Hide items beyond threshold
        allItems.forEach((item, index) => {
          if (index >= threshold) {
            item.classList.add('show-more-item', 'hidden');
          }
        });
        showMoreButton.style.display = '';
      } else {
        showMoreButton.style.display = 'none';
      }
    }
  }

  getApiVersion() {
    // Try to get API version from Shopify global or use latest stable
    return window.Shopify?.api_version || '2025-01';
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  escapeGraphQL(text) {
    return text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
  }

  /**
   * Update active facets to show dynamically loaded filter values
   * This handles cases where selected filter values exceed the 100-item Liquid limit
   */
  async updateActiveFacets() {
    const activeFacetsContainers = document.querySelectorAll('.active-facets');
    if (activeFacetsContainers.length === 0) return;

    console.log('🏷️ Updating active facets for dynamic filters...');

    // Remove old dynamic facets first
    document.querySelectorAll('facet-remove[data-dynamic-facet="true"]').forEach(el => el.remove());

    // Get URL parameters to find active filters
    const urlParams = new URLSearchParams(window.location.search);
    const activeFilters = new Map(); // Map of filter key -> Set of values

    for (const [key, value] of urlParams.entries()) {
      if (key.includes('filter.') && value && value.trim() !== '') {
        if (!activeFilters.has(key)) {
          activeFilters.set(key, new Set());
        }
        activeFilters.get(key).add(value);
      }
    }

    if (activeFilters.size === 0) return;

    // Fetch all filter data to get labels for active filters
    try {
      const cacheKey = `${this.collectionHandle || this.searchQuery}_all`;
      let filterData = this.filterCache.get(cacheKey);

      if (!filterData) {
        filterData = await this.fetchFilterValues();
        this.filterCache.set(cacheKey, filterData);
      }

      // For each active filter, check if it exists in the DOM
      for (const [filterKey, filterValues] of activeFilters.entries()) {
        const filter = this.findFilterByKey(filterData, filterKey);
        if (!filter) continue;

        for (const filterValue of filterValues) {
          // Check if this active facet already exists in the DOM
          const existsInDom = this.activeFacetExistsInDom(filter.label, filterValue);
          
          if (!existsInDom) {
            // This is a dynamically loaded filter value, add it to active facets
            const valueData = this.findFilterValueData(filter, filterValue);
            // Even if valueData is not found, still show the facet with the raw value
            this.renderActiveFacet(activeFacetsContainers[0], filter, valueData || { label: filterValue }, filterKey, filterValue);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to update active facets:', error);
    }
  }

  /**
   * Find filter by parameter key (e.g., "filter.p.m.custom.dodatki")
   */
  findFilterByKey(filters, filterKey) {
    // Extract meaningful parts from the key
    if (filterKey.includes('.m.')) {
      // Metafield filter: filter.p.m.{namespace}.{key}
      const parts = filterKey.split('.');
      const namespaceIndex = parts.indexOf('m') + 1;
      const namespace = parts[namespaceIndex];
      const key = parts[namespaceIndex + 1];
      
      return filters.find(f => {
        return f.id && f.id.includes(`.m.${namespace}.${key}`);
      });
    } else if (filterKey.includes('.product_type')) {
      return filters.find(f => f.id === 'filter.p.product_type');
    } else if (filterKey.includes('.vendor')) {
      return filters.find(f => f.id === 'filter.p.vendor');
    } else if (filterKey.includes('.tag')) {
      return filters.find(f => f.id === 'filter.p.tag');
    } else if (filterKey.includes('.option.')) {
      const optionName = filterKey.split('.option.')[1];
      return filters.find(f => f.id && f.id.includes(`.option.${optionName}`));
    }
    
    return null;
  }

  /**
   * Find the filter value data object by its value
   */
  findFilterValueData(filter, value) {
    if (!filter.values) return null;
    
    return filter.values.find(v => {
      try {
        const inputData = typeof v.input === 'string' ? JSON.parse(v.input) : v.input;
        const filterValue = this.getFilterValue(inputData);
        return filterValue === value;
      } catch (e) {
        return false;
      }
    });
  }

  /**
   * Check if an active facet already exists in the DOM
   */
  activeFacetExistsInDom(filterLabel, filterValue) {
    const existingFacets = document.querySelectorAll('facet-remove a');
    
    for (const facet of existingFacets) {
      const text = facet.textContent;
      // Check if the facet contains both the filter label and value
      if (text.includes(`${filterLabel}:`) && text.includes(filterValue)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Render a single active facet pill
   */
  renderActiveFacet(container, filter, valueData, filterKey, filterValue) {
    // Find the "Clear all" button to insert before it
    const clearAllButton = container.querySelector('.active-facets__button-wrapper');
    
    if (!clearAllButton) {
      console.warn('Clear all button not found, cannot add active facet');
      return;
    }

    // Create the active facet element
    const facetRemove = document.createElement('facet-remove');
    facetRemove.setAttribute('data-dynamic-facet', 'true');
    
    // Build the URL to remove this filter
    const currentUrl = new URLSearchParams(window.location.search);
    const allValues = currentUrl.getAll(filterKey);
    
    // Remove this specific value
    currentUrl.delete(filterKey);
    allValues.filter(v => v !== filterValue).forEach(v => {
      currentUrl.append(filterKey, v);
    });
    
    const removeUrl = `${window.location.pathname}?${currentUrl.toString()}`;
    
    // Create the link
    const link = document.createElement('a');
    link.href = removeUrl;
    link.className = 'active-facets__button active-facets__button--light';
    link.setAttribute('data-no-instant', '');
    
    link.innerHTML = `
      <span class="active-facets__button-inner button button--tertiary">
        ${this.escapeHtml(filter.label)}: ${this.escapeHtml(valueData.label)}
        <svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="visually-hidden">Remove filter</span>
      </span>
    `;
    
    facetRemove.appendChild(link);
    
    // Insert before the "Clear all" button
    container.insertBefore(facetRemove, clearAllButton);
    
    // Manually initialize the click handler since custom element might not auto-init
    // This mimics the behavior of the FacetRemove custom element
    link.setAttribute('role', 'button');
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const form = facetRemove.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      if (form && form.onActiveFilterClick) {
        form.onActiveFilterClick(event);
      }
    });
    
    link.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') {
        const form = facetRemove.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        if (form && form.onActiveFilterClick) {
          form.onActiveFilterClick(event);
        }
      }
    });
    
    console.log(`✅ Added dynamic active facet: ${filter.label}: ${valueData.label}`);
  }
}

// Initialize on page load
if (!customElements.get('dynamic-facet-loader')) {
  class DynamicFacetLoaderElement extends HTMLElement {
    connectedCallback() {
      if (!window.dynamicFacetLoader) {
        window.dynamicFacetLoader = new DynamicFacetLoader();
      }
    }
  }
  
  customElements.define('dynamic-facet-loader', DynamicFacetLoaderElement);
}

// Also initialize immediately if not using custom element
document.addEventListener('DOMContentLoaded', () => {
  if (!window.dynamicFacetLoader) {
    window.dynamicFacetLoader = new DynamicFacetLoader();
  }
});

